import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, getTracking } from "@/lib/api";
import { GARMENT_TYPES, OPTION_SETS, fabricImg, estimatePrice, fmtPrice } from "@/lib/designOptions";

const EASE = [0.16, 1, 0.3, 1];
const inputCls =
  "w-full border border-[#E5DEC9] bg-white px-4 py-3 text-sm text-[#1A1917] outline-none transition-colors focus:border-[#1A1917]";

const defaultOptions = (garment) =>
  Object.fromEntries(OPTION_SETS[garment].map((o) => [o.key, o.values[0]]));

export default function DesignConfigurator({ fabric, onClose }) {
  const [garment, setGarment] = useState("suit");
  const [options, setOptions] = useState(defaultOptions("suit"));
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const pickGarment = (id) => {
    setGarment(id);
    setOptions(defaultOptions(id));
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const estimate = estimatePrice(fabric, garment, options);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the made-to-measure process to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/design-requests", {
        garment,
        fabric_id: fabric.fabric_id,
        options,
        ...form,
        process_agreed: agreed,
        tracking: getTracking(),
      });
      setResult(data);
      toast.success("Your design has been received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] bg-[#121110]/60 backdrop-blur-sm"
      data-testid="design-configurator"
    >
      <div
        data-lenis-prevent
        className="absolute inset-x-0 bottom-0 top-0 overflow-y-auto sm:inset-x-6 sm:top-6 lg:inset-x-16"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="mx-auto min-h-full max-w-6xl bg-white"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#E5DEC9] bg-white/95 px-6 py-4 backdrop-blur sm:px-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D38]">
              The Design Studio
            </p>
            <button
              data-testid="design-configurator-close"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center border border-[#E5DEC9] text-[#524F4A] transition-colors hover:border-[#1A1917] hover:text-[#1A1917]"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {result ? (
            <div className="px-6 py-14 sm:px-10" data-testid="design-success">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2D5A3F]">
                <Check size={22} className="text-white" />
              </div>
              <h2 className="mt-8 font-serif text-4xl font-light tracking-tight text-[#1A1917]">
                Your design is in the atelier.
              </h2>
              <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-[#524F4A]">
                Thank you, {result.name.split(" ")[0]}. Reza will contact you personally to confirm
                cloth availability and pricing. The next step is your measurement session — book it
                now, or we will reach out to arrange it.
              </p>
              <div className="mt-8 border-t border-[#EDE8DB] pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Reference</p>
                <p className="mt-1 font-mono text-sm tracking-[0.15em] text-[#8C6D38]" data-testid="design-success-ref">
                  {result.request_id}
                </p>
                {result.estimated_from && (
                  <p className="mt-3 text-sm font-light text-[#524F4A]" data-testid="design-success-estimate">
                    Estimated from {fmtPrice(result.estimated_from)} — final quote at your consultation.
                  </p>
                )}
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/book"
                  data-testid="design-success-book-cta"
                  className="inline-flex items-center gap-3 bg-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#8C6D38]"
                >
                  Book Your Measurement Session <ArrowRight size={14} />
                </Link>
                <button
                  data-testid="design-success-close"
                  onClick={onClose}
                  className="border border-[#1A1917] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1A1917] transition-colors hover:bg-[#1A1917] hover:text-white"
                >
                  Keep Browsing Cloths
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-10 px-6 py-10 sm:px-10 lg:grid-cols-12">
              {/* Fabric summary */}
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-24">
                  <div className="overflow-hidden border border-[#EDE8DB]">
                    {fabricImg(fabric) ? (
                      <img
                        src={fabricImg(fabric)}
                        alt={fabric.name}
                        className="aspect-square w-full object-cover"
                        data-testid="design-configurator-fabric-image"
                      />
                    ) : (
                      <div className="flex aspect-square w-full items-center justify-center bg-[#F5F2EA] font-mono text-[10px] uppercase tracking-[0.2em] text-[#858078]">
                        Swatch coming soon
                      </div>
                    )}
                  </div>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C6D38]">{fabric.mill}</p>
                  <h2 className="mt-1 font-serif text-3xl font-normal text-[#1A1917]" data-testid="design-configurator-fabric-name">
                    {fabric.name}
                  </h2>
                  <p className="mt-2 text-sm font-light text-[#524F4A]">
                    {fabric.composition} · {fabric.color} · {fabric.pattern}
                  </p>
                  {estimate && (
                    <div className="mt-6 border border-[#E5DEC9] bg-[#FBF9F5] p-5" data-testid="design-estimate-panel">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Estimated from</p>
                      <p className="mt-1 font-serif text-3xl font-light tracking-tight text-[#1A1917]" data-testid="design-estimated-price">
                        {fmtPrice(estimate)}
                      </p>
                      <p className="mt-2 text-xs font-light leading-relaxed text-[#858078]">
                        Updates with your choices. Final quote confirmed by Reza at your consultation.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Configuration */}
              <form onSubmit={submit} className="lg:col-span-7">
                <h3 className="font-serif text-2xl font-normal text-[#1A1917]">1 · Choose your garment</h3>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {GARMENT_TYPES.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      data-testid={`design-garment-${g.id}`}
                      onClick={() => pickGarment(g.id)}
                      className={`border p-4 text-left transition-colors ${
                        garment === g.id
                          ? "border-[#1A1917] bg-[#1A1917] text-white"
                          : "border-[#E5DEC9] text-[#1A1917] hover:border-[#1A1917]"
                      }`}
                    >
                      <p className="font-serif text-lg">{g.name}</p>
                      <p className={`mt-1 text-xs font-light ${garment === g.id ? "text-white/70" : "text-[#858078]"}`}>
                        {g.desc}
                      </p>
                    </button>
                  ))}
                </div>

                <h3 className="mt-12 font-serif text-2xl font-normal text-[#1A1917]">2 · Shape the details</h3>
                <div className="mt-5 space-y-6">
                  {OPTION_SETS[garment].map((o) => (
                    <div key={o.key} data-testid={`design-option-${o.key.toLowerCase()}`}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{o.key}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {o.values.map((v) => (
                          <button
                            key={v}
                            type="button"
                            data-testid={`design-option-${o.key.toLowerCase()}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => setOptions({ ...options, [o.key]: v })}
                            className={`border px-4 py-2 text-xs transition-colors ${
                              options[o.key] === v
                                ? "border-[#8C6D38] bg-[#8C6D38]/10 text-[#8C6D38]"
                                : "border-[#E5DEC9] text-[#524F4A] hover:border-[#1A1917] hover:text-[#1A1917]"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <h3 className="mt-12 font-serif text-2xl font-normal text-[#1A1917]">3 · The made-to-measure process</h3>
                <div className="mt-5 border border-[#E5DEC9] bg-[#FBF9F5] p-6">
                  <ul className="space-y-3 text-sm font-light leading-relaxed text-[#524F4A]">
                    <li className="flex gap-3"><Check size={15} className="mt-0.5 shrink-0 text-[#8C6D38]" />
                      Your measurements are taken in person in San Diego, or through a guided video session.</li>
                    <li className="flex gap-3"><Check size={15} className="mt-0.5 shrink-0 text-[#8C6D38]" />
                      A basted (trial) garment is made and sent to you — a local stylist meets you to fit and re-measure it before the final garment is cut.</li>
                    <li className="flex gap-3"><Check size={15} className="mt-0.5 shrink-0 text-[#8C6D38]" />
                      Each garment can take up to eight weeks to complete.</li>
                  </ul>
                  <label className="mt-5 flex cursor-pointer items-start gap-3 border-t border-[#EDE8DB] pt-5">
                    <input
                      type="checkbox"
                      data-testid="design-process-agree-checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[#8C6D38]"
                    />
                    <span className="text-sm font-light leading-relaxed text-[#1A1917]">
                      I understand and agree to the measurement process, the basted fitting with a
                      local stylist, and the up-to-eight-week timeline.
                    </span>
                  </label>
                </div>

                <h3 className="mt-12 font-serif text-2xl font-normal text-[#1A1917]">4 · Where do we reach you?</h3>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <Field label="Full Name">
                    <input data-testid="design-input-name" required value={form.name} onChange={set("name")} placeholder="James Sterling" className={inputCls} />
                  </Field>
                  <Field label="Email">
                    <input data-testid="design-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="design-input-phone" required value={form.phone} onChange={set("phone")} placeholder="(858) 555-0100" className={inputCls} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Anything else — occasion, timeline, second cloth choice">
                      <textarea data-testid="design-input-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="A navy suit for an October wedding…" className={inputCls} />
                    </Field>
                  </div>
                </div>

                <button
                  data-testid="design-submit-button"
                  type="submit"
                  disabled={submitting || !agreed}
                  className="mt-10 inline-flex items-center gap-3 bg-[#8C6D38] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#1A1917] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {submitting ? "Sending…" : "Submit My Design"} <ArrowRight size={14} />
                </button>
                {!agreed && (
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#858078]">
                    Agree to the process above to submit
                  </p>
                )}
                <div className="h-10" />
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</span>
      {children}
    </label>
  );
}
